import * as React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowSquareOut as ArrowSquareOutIcon,
  Envelope as EnvelopeIcon,
  House as HouseIcon,
  MapPin as MapPinIcon,
  Users as UsersIcon,
} from '@phosphor-icons/react';

import type { Request } from '@/types/v2/request';
import { useUser } from '@/hooks/v2/use-user';
import { paths } from '@/paths';
import { dayjs } from '@/lib/v2/dayjs';

// ─── Shared section label ─────────────────────────────────────────────────────

export function SectionLabel({ icon, children, action }: { icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {icon && <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          {children}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );
}

// ─── useCanViewDetails ────────────────────────────────────────────────────────

export function useCanViewDetails() {
  const { user, account, community } = useUser();

  const hasRole = (roles: string[], source: 'user' | 'account' | 'community') => {
    if (source === 'user') return user?.roles?.some((r) => roles.includes(r.name)) ?? false;
    if (source === 'account') return account?.roles?.some((r) => roles.includes(r)) ?? false;
    return community?.roles?.some((r) => roles.includes(r)) ?? false;
  };

  return (
    hasRole(['super_admin', 'platform_admin'], 'user') ||
    hasRole(['account_owner', 'account_admin'], 'account') ||
    hasRole(['community_manager', 'board_member'], 'community')
  );
}

// ─── LEFT COLUMN: Description ─────────────────────────────────────────────────

interface RequestDescriptionProps {
  request?: Request | null;
  isLoading: boolean;
}

export function RequestDescription({ request, isLoading }: RequestDescriptionProps): React.JSX.Element {
  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} width="30%" variant="text" />
        <Skeleton height={60} variant="rectangular" sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  return (
    <Box>
      <SectionLabel>Description</SectionLabel>
      <Box sx={{ mt: 1.5 }}>
        {request?.description ? (
          <Typography
            color="text.primary"
            sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: '0.9375rem' }}
            variant="body2"
          >
            {request.description}
          </Typography>
        ) : (
          <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
            No description provided.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── RIGHT COLUMN: Sidebar ────────────────────────────────────────────────────

interface RequestSidebarProps {
  request?: Request | null;
  isLoading: boolean;
}

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography
        color="text.secondary"
        sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 90 }}
        variant="body2"
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right' }} variant="body2">
        {children}
      </Typography>
    </Stack>
  );
}

export function RequestSidebar({ request, isLoading }: RequestSidebarProps): React.JSX.Element {
  const canViewDetails = useCanViewDetails();

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={18} variant="text" />
        ))}
      </Stack>
    );
  }

  const unit = request?.unit;
  const residents = unit?.current_residents ?? [];
  const showUnit = unit && request?.unit_id && canViewDetails;

  return (
    <Stack divider={<Divider />} spacing={0}>
      {/* ── Key metadata ── */}
      <Box sx={{ pb: 2 }}>
        <SectionLabel>Details</SectionLabel>
        <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
          <KVRow label="Submitted">
            {request?.created_at ? dayjs(request.created_at).format('MMM D, YYYY, h:mm A') : '—'}
          </KVRow>
          {request?.experienced_on && (
            <KVRow label="Incident date">
              {dayjs(request.experienced_on).format('MMM D, YYYY')}
            </KVRow>
          )}
          {request?.created_by && (
            <KVRow label="Submitted by">
              {canViewDetails && request.created_by.uuid ? (
                <Link
                  href={paths.users.details(request.created_by.uuid)}
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {request.created_by.first_name} {request.created_by.last_name}
                </Link>
              ) : (
                `${request.created_by.first_name} ${request.created_by.last_name}`
              )}
            </KVRow>
          )}
          {request?.assigned_to && (
            <KVRow label="Assigned to">
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                <Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                  {request.assigned_to.first_name?.[0]}
                </Avatar>
                <span>{request.assigned_to.first_name} {request.assigned_to.last_name}</span>
              </Stack>
            </KVRow>
          )}
        </Stack>
      </Box>

      {/* ── Property ── */}
      {showUnit && (
        <Box sx={{ py: 2 }}>
          <SectionLabel
            icon={<HouseIcon size={14} />}
            action={
              <Link
                href={paths.units.details(unit.uuid ?? unit.id?.toString())}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                View
                <ArrowSquareOutIcon size={11} />
              </Link>
            }
          >
            Property
          </SectionLabel>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {unit.unit_number ? `Unit ${unit.unit_number}` : 'Unit'}
            </Typography>
            {(unit.address || unit.city) && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start', color: 'text.secondary', mt: 0.5 }}>
                <MapPinIcon size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  {[unit.address, unit.city, unit.state, unit.zip_code].filter(Boolean).join(', ')}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      {/* ── Residents ── */}
      {showUnit && (
        <Box sx={{ py: 2 }}>
          <SectionLabel icon={<UsersIcon size={14} />}>
            Residents
            {residents.length > 0 && (
              <Chip
                label={residents.length}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem', ml: 1 }}
                variant="outlined"
              />
            )}
          </SectionLabel>
          <Box sx={{ mt: 1.5 }}>
            {residents.length === 0 ? (
              <Typography color="text.disabled" sx={{ fontStyle: 'italic' }} variant="body2">
                No residents assigned.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {residents.map((resident: any) => {
                  const content = (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        transition: 'background-color 0.12s',
                        ...(resident.uuid && { '&:hover': { bgcolor: 'action.hover' } }),
                      }}
                    >
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'primary.main' }}>
                        {resident.first_name?.[0]}{resident.last_name?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                          {resident.first_name} {resident.last_name}
                        </Typography>
                        {resident.email && (
                          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                            <EnvelopeIcon size={10} />
                            <Typography noWrap sx={{ fontSize: '0.7rem' }}>{resident.email}</Typography>
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  );
                  return resident.uuid ? (
                    <Link href={paths.users.details(resident.uuid)} key={resident.id} underline="none" sx={{ color: 'inherit' }}>
                      {content}
                    </Link>
                  ) : (
                    <React.Fragment key={resident.id}>{content}</React.Fragment>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
